import { useJumboDialog } from '@jumbo/components/JumboDialog/hooks/useJumboDialog';
import useJumboAuth from '@jumbo/hooks/useJumboAuth';
import ArticleIcon from "@mui/icons-material/Article";
import EditIcon from '@mui/icons-material/Edit';
import { Button, Pagination } from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import subTaskService from 'app/services/subtask_service';
import moment from 'moment';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useSearchParams } from 'react-router-dom'; // Import useSearchParams
import ExpenseDocumentBox from '../HrOfficeExpanses/ExpenseDocumentBox';
import ExpenseApprovalFinanceForm from './ExpenseApprovalFinanceForm';
import FinancePaymentStatusForm from './FinancePaymentStatusForm';
import { expenseApprovalData, hrOfficeApproveExpenseCleanUp } from 'app/redux/actions/subTaskAction';
import Swal from 'sweetalert2';
import { docBaseUrl } from 'app/services/config';

// Helper function to create filter query string
const createFilterString = (filterModel) => {
  if (!filterModel?.items) return '';
  return filterModel.items
    .filter(item => item.field !== undefined)
    .map(item => `&${item.field}=${item.value || ''}`)
    .join('');
};

// Helper function for CSV conversion (unmodified)
const convertToCSV = (objArray) => {
  const array = typeof objArray !== 'object' ? JSON.parse(objArray) : objArray;
  let csv = '';

  for (const row of array) {
    const line = Object.values(row).map(fieldValue => {
      let value = fieldValue;
      if (Array.isArray(value)) {
        value = value.join('\n');
      } else if (typeof value === 'string' && value.startsWith('0')) {
        value = `"${value}"`;
      }
      if (typeof value === 'string' && (value.includes(',') || value.includes('\n') || value.includes('"'))) {
        value = `"${value.replace(/"/g, '""')}"`;
      }
      return value;
    }).join(',');
    csv += line + '\r\n';
  }
  return csv;
};

// Helper function to handle CSV export (unmodified)
const exportCSVFile = (headers, items, fileTitle) => {
  if (headers) {
    items.unshift(headers);
  }
  const jsonObject = JSON.stringify(items);
  const csv = convertToCSV(jsonObject);
  const exportedFilenmae = `${fileTitle}.csv` || 'export.csv';
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });

  if (navigator.msSaveBlob) {
    navigator.msSaveBlob(blob, exportedFilenmae);
  } else {
    const link = document.createElement("a");
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", exportedFilenmae);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }
};

const CHIP_COLORS = {
  'Requested': 'blue',
  'Approved': 'green',
  'Rejected': 'red',
  'default': 'gray'
};

const EXPENSE_LIMIT = 10;

export default function EmployeeExpenseApprove() {
  const dispatch = useDispatch();
  const { setAuthToken } = useJumboAuth();
  const roleCode = localStorage.getItem("roleCode");
  const { employeeOfficeExpensesData } = useSelector(state => state.typereducer);
  const { hideDialog, showDialog } = useJumboDialog();
  const [rowSelectionModel, setRowSelectionModel] = React.useState([]);
  const [totalCount, setTotalCount] = React.useState(0);

  // Use useSearchParams hook to manage URL query parameters
  const [searchParams, setSearchParams] = useSearchParams();

  // Get initial page and filters from URL or use defaults
  const initialPage = parseInt(searchParams.get('page') || '1', 10);
  const initialFilterModel = JSON.parse(searchParams.get('filter') || '{}');
  const [page, setPage] = React.useState(initialPage);
  const [queryOptions, setQueryOptions] = React.useState({ filterModel: initialFilterModel });

  // Memoize the rows array to prevent re-computation on every render
  const rows = React.useMemo(() => {
    let _id = (page - 1) * EXPENSE_LIMIT + 1;
    return employeeOfficeExpensesData.map(item => ({
      id: _id++,
      expenseId: item?.id,
      employeeName: item?.employee_id.name,
      expenseAmount: item?.expense_amount,
      expenseDate: item?.expense_date ? moment(item?.expense_date).format('DD MMM YYYY') : '',
      expenseDescription: item?.expense_description || '--',
      expenseNote: item?.expense_note || '--',
      expenseDoc: item?.bill_reciept_documents || [],
      status: item?.status || 'Requested',
      paymentStatus: item?.payment_status || 'Unpaid',
      paidBy: item?.paid_by || '--',
      paymentDate: item?.payment_date ? moment(item?.payment_date).format('DD MMM YYYY') : '',
      hrFinanceStatus: item?.hr_finance_status || '--',
      actionRemark: item?.action_remark || '--',
      company: item?.company || '--',
      projects: item?.projects || '--',
    }));
  }, [employeeOfficeExpensesData, page]);

  const showExpenseDialog = React.useCallback((expenseDoc) => {
    showDialog({
      title: "Expense Documents",
      content: <ExpenseDocumentBox expenseDoc={expenseDoc} />
    });
  }, [showDialog]);

  const handleUpdate = React.useCallback(() => {
    hideDialog();
  }, [hideDialog]);

  const showApproveExpenseDialog = React.useCallback((expenseId, hrFinanceStatus) => {
    showDialog({
      title: "Expense",
      content: <ExpenseApprovalFinanceForm expenseId={expenseId} hrFinanceStatus={hrFinanceStatus} onSave={handleUpdate} />
    });
  }, [handleUpdate, showDialog]);

  const showPaymentStatusDialog = React.useCallback((expenseIds, hrFinanceStatus) => {
    showDialog({
      title: "Add Payment Status",
      content: <FinancePaymentStatusForm expenseIds={expenseIds} hrFinanceStatus={hrFinanceStatus} onSave={handleUpdate} />
    });
  }, [handleUpdate, showDialog]);

  const handleExport = React.useCallback(() => {
    const filterQuery = createFilterString(queryOptions.filterModel);
    subTaskService.getExpensesApprovals('ApproveExpensesFinance', filterQuery)
      .then((data) => {
        const mockData = data?.data?.data?.map((item, index) => ({
          "id": index + 1,
          "employeeName": item?.employee_id?.name || '--',
          "status": item?.status || 'Requested',
          "hrFinanceStatus": item?.hr_finance_status || '--',
          "actionRemark": item?.action_remark || "--",
          "actionBy": item?.action_by?.name || '--',
          "hrActionBy": item?.hr_action_by?.name || '--',
          "paymentStatus": item?.payment_status || 'Unpaid',
          "paidBy": item?.paid_by || '--',
          "paymentDate": item?.payment_date ? moment(item?.payment_date).format('DD MMM YYYY') : '',
          "expenseAmount": item?.expense_amount || '--',
          "expenseDate": moment(item?.expense_date).format('DD MMM YYYY') || '--',
          "company": item?.company || '--',
          "projects": item?.projects || '--',
          "expenseDescription": item?.expense_description || '--',
          "expenseNote": item?.expense_note || '--',
          "expenseDoc": item?.bill_reciept_documents?.map(doc => `${docBaseUrl}${doc?.base_url}/${doc?.key}`) || '--',
        }));

        const headers = {
          id: "ID", employeeName: "Employee Name", status: "Manager Status", hrFinanceStatus: "HR/Finance Status",
          actionRemark: "Remark", actionBy: "Approved By(Manager)", hrActionBy: "Approved By(hr/finance)",
          paymentStatus: "Payment Status", paidBy: "Paid From", paymentDate: "Payment Date",
          expenseAmount: "Expense Amount", expenseDate: "Expense Date", company: 'Company', projects: 'Projects', expenseDescription: "Expense Description",
          expenseNote: "Note", expenseDoc: "Documents"
        };
        exportCSVFile(headers, mockData, 'Employee Expense Export');
      })
      .catch((err) => {
        if (err?.response?.status === 401) setAuthToken(null);
      });
  }, [queryOptions, setAuthToken]);

  const onFilterChange = React.useCallback((filterModel) => {
    setQueryOptions({ filterModel: { ...filterModel } });
    if (filterModel?.items?.[0]?.value !== undefined) {
      setPage(1); // Reset to page 1 on filter change
    }
  }, []);

  const handlePaginationChange = (event, value) => {
    setPage(value);
  };

  const handleBulkApproval = () => {
    const expenseIds = rowSelectionModel.map(item => item.expenseId);
    showPaymentStatusDialog(expenseIds, 'Approved');
  };

  const handleCellClick = (params) => {
    const { field, value } = params;
    if (!['id', '__check__', 'expenseDoc', 'paymentStatus'].includes(field)) {
      Swal.fire({
        html: `<div style="white-space: pre-wrap;">${value}</div>`,
        showConfirmButton: false,
        customClass: { popup: 'my-custom-size' },
        didOpen: () => {
          const swalPopup = Swal.getPopup();
          if (swalPopup) swalPopup.style.width = '400px';
        },
      });
    }
  };

  React.useEffect(() => {
    // Update URL with current state
    const params = {};
    if (page !== 1) {
      params.page = page;
    }
    if (Object.keys(queryOptions.filterModel).length > 0) {
      params.filter = JSON.stringify(queryOptions.filterModel);
    }
    setSearchParams(params);

    // Fetch data from API based on current state
    const offset = (page - 1) * EXPENSE_LIMIT;
    const filterString = createFilterString(queryOptions.filterModel);
    subTaskService.getExpensesApprovals('ApproveExpensesFinance', offset, EXPENSE_LIMIT, filterString)
      .then((data) => {
        setTotalCount(data?.data?.count);
        dispatch(expenseApprovalData(data?.data?.data));
      })
      .catch((err) => {
        if (err?.response?.status === 401) setAuthToken(null);
      });

    return () => {
      dispatch(hrOfficeApproveExpenseCleanUp());
    };
  }, [page, queryOptions, dispatch, setAuthToken, setSearchParams]); // Add setSearchParams to dependencies

  // Columns definition remains the same
  const getColumns = React.useMemo(() => [
    {
      field: 'id', headerName: 'ID', sortable: false, filterable: false, flex: 100, align: 'center', headerAlign: 'center',
      renderCell: (params) => (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span>{params.value}</span>
          <NavLink>
            <EditIcon
              onClick={() => {
                const isEditable = params.row.status === 'Approved' && params.row.hrFinanceStatus === 'Requested' && (roleCode === 'ACCOUNTSMANAGER' || roleCode === 'FINANCEMANAGER');
                if (isEditable) showApproveExpenseDialog(params.row.expenseId, params.row.hrFinanceStatus);
              }}
              style={{
                width: '20px', height: '20px', marginLeft: '5px',
                cursor: (params.row.status === 'Approved' && params.row.hrFinanceStatus === 'Requested' && (roleCode === 'ACCOUNTSMANAGER' || roleCode === 'FINANCEMANAGER')) ? 'pointer' : 'not-allowed'
              }}
              color="action"
            />
          </NavLink>
        </div>
      ),
      renderHeader: () => (<div style={{ whiteSpace: 'normal', wordWrap: 'break-word', textAlign: 'center', lineHeight: '1', marginTop: '2px' }}>ID</div>)
    },
    {
      field: 'employeeName', headerName: 'Employee Name', sortable: false, flex: 220, align: 'center', headerAlign: 'center',
      renderHeader: () => (<div style={{ whiteSpace: 'normal', wordWrap: 'break-word', textAlign: 'center', lineHeight: '1', marginTop: '2px' }}>Employee Name</div>)
    },
    {
      field: 'status', headerName: 'Manager Status', sortable: false, flex: 300, align: 'center', headerAlign: 'center',
      renderCell: (params) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ width: 10, height: 10, backgroundColor: CHIP_COLORS[params.value] || CHIP_COLORS.default, marginRight: 8, borderRadius: '100%' }}></div>
          {params.value}
        </div>
      ),
      renderHeader: () => (<div style={{ whiteSpace: 'normal', wordWrap: 'break-word', textAlign: 'center', lineHeight: '1', marginTop: '2px' }}>Manager Status</div>)
    },
    {
      field: 'hrFinanceStatus', headerName: 'HR/Finance Status', sortable: false, flex: 300, align: 'center', headerAlign: 'center',
      renderCell: (params) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ width: 10, height: 10, backgroundColor: CHIP_COLORS[params.value] || CHIP_COLORS.default, marginRight: 8, borderRadius: '100%' }}></div>
          {params.value}
        </div>
      ),
      renderHeader: () => (<div style={{ whiteSpace: 'normal', wordWrap: 'break-word', textAlign: 'center', lineHeight: '1', marginTop: '2px' }}>HR/Finance Status</div>)
    },
    {
      field: 'actionRemark', headerName: 'Remark', filterable: false, sortable: false, flex: 300, align: 'center', headerAlign: 'center',
      renderHeader: () => (<div style={{ whiteSpace: 'normal', wordWrap: 'break-word', textAlign: 'center', lineHeight: '1', marginTop: '2px' }}>Remark</div>)
    },
    {
      field: 'expenseAmount', headerName: 'Expense Amount', sortable: false, filterable: false, flex: 220, align: 'center', headerAlign: 'center',
      renderHeader: () => (<div style={{ whiteSpace: 'normal', wordWrap: 'break-word', textAlign: 'center', lineHeight: '1', marginTop: '2px' }}>Expense Amount</div>)
    },
    {
      field: 'expenseDate', headerName: 'Expense Date', sortable: false, filterable: false, flex: 220, align: 'center', headerAlign: 'center',
      renderHeader: () => (<div style={{ whiteSpace: 'normal', wordWrap: 'break-word', textAlign: 'center', lineHeight: '1', marginTop: '2px' }}>Expense Date</div>)
    },
    {
      field: 'paymentStatus', headerName: 'Payment Status', type: 'singleSelect', valueOptions: ['Paid', 'Unpaid'], sortable: false, flex: 300, align: 'center', headerAlign: 'center',
      renderCell: (params) => {
        const isActionable = params.row.hrFinanceStatus === 'Approved' && params.row.paymentStatus !== 'Paid' && ['FINANCEMANAGER', 'ACCOUNTSMANAGER'].includes(roleCode);
        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Button size="small" onClick={isActionable ? () => showPaymentStatusDialog(params.row.expenseId, params.row.hrFinanceStatus) : undefined} disabled={!isActionable}>
              {params.value}
            </Button>
          </div>
        );
      },
      renderHeader: () => (<div style={{ whiteSpace: 'normal', wordWrap: 'break-word', textAlign: 'center', lineHeight: '1', marginTop: '2px' }}>Payment Status</div>)
    },
    {
      field: 'paymentDate', headerName: 'Payment Date', type: 'date', sortable: false, flex: 300, align: 'center', headerAlign: 'center',
      valueGetter: (params) => {
        const date = new Date(params.value);
        return isNaN(date.getTime()) ? null : date;
      },
      renderHeader: () => (<div style={{ whiteSpace: 'normal', wordWrap: 'break-word', textAlign: 'center', lineHeight: '1', marginTop: '2px' }}>Payment Date</div>)
    },
    { field: 'projects', headerName: 'Projects', filterable: true, sortable: false, flex: 300, align: 'center', headerAlign: 'center' },
    { field: 'company', headerName: 'Company', filterable: true, sortable: false, flex: 300, align: 'center', headerAlign: 'center' },
    {
      field: 'expenseDescription', headerName: 'Description', filterable: false, sortable: false, flex: 300, align: 'center', headerAlign: 'center',
      renderHeader: () => (<div style={{ whiteSpace: 'normal', wordWrap: 'break-word', textAlign: 'center', lineHeight: '1', marginTop: '2px' }}>Description</div>)
    },
    {
      field: 'expenseNote', headerName: 'Note', filterable: false, sortable: false, flex: 300, align: 'center', headerAlign: 'center',
      renderHeader: () => (<div style={{ whiteSpace: 'normal', wordWrap: 'break-word', textAlign: 'center', lineHeight: '1', marginTop: '2px' }}>Note</div>)
    },
    {
      field: 'expenseDoc', headerName: 'Documents', filterable: false, sortable: false, flex: 300, align: 'center', headerAlign: 'center',
      renderCell: (params) => (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {params.row.expenseDoc.length > 0 && (
            <ArticleIcon onClick={() => showExpenseDialog(params.value)} style={{ width: '20px', height: '20px', marginLeft: '5px' }} color="action" />
          )}
        </div>
      ),
      renderHeader: () => (<div style={{ whiteSpace: 'normal', wordWrap: 'break-word', textAlign: 'center', lineHeight: '1', marginTop: '2px' }}>Documents</div>)
    }
  ], [roleCode, showApproveExpenseDialog, showPaymentStatusDialog, showExpenseDialog]);

  return (
    <>
      <div style={{ height: '60px', width: '100%' }}>
        {(['FINANCEMANAGER','ACCOUNTSMANAGER'].includes(roleCode) && rowSelectionModel.every(emp => emp.hrFinanceStatus === 'Approved')) && rowSelectionModel.length > 0 && (
          <Button variant="contained" onClick={handleBulkApproval}>
            Payment Approval
          </Button>
        )}
        <Button onClick={handleExport} sx={{ float: 'right' }} variant="contained">
          Export Expenses
        </Button>
      </div>
      <div style={{ height: '100%', width: '100%' }}>
        <DataGridPro
          checkboxSelection
          isRowSelectable={(params) => params.row.paymentStatus === "Unpaid" && params.row.hrFinanceStatus !== "Rejected"}
          sx={{ backgroundColor: '#F6F4FA' }}
          rows={rows}
          columns={getColumns}
          pageSize={30}
          autoPageSize
          autoHeight
          density='standard'
          filterMode='server'
          onFilterModelChange={onFilterChange}
          hideFooter
          onRowSelectionModelChange={(newRowSelectionModel) => {
            const selectedRows = rows.filter(item => newRowSelectionModel.includes(item.id));
            setRowSelectionModel(selectedRows);
          }}
          rowSelectionModel={rowSelectionModel.map(i => i.id)}
          onCellClick={handleCellClick}
        />
        <Pagination count={Math.ceil(totalCount / EXPENSE_LIMIT)} sx={{ mt: 3, float: 'right', color: "#F6F4FA" }} page={page} onChange={handlePaginationChange} />
      </div>
    </>
  );
}